/** tm/components/AIBizplanModal.tsx — AI 사업계획서 (섹션별) 모달 */
import React, { useState, useMemo } from 'react';
import { C, T, R } from '../components/ui';
import type { AIPreset, TaskStep, Task, Company } from '../types';

export interface BPSection {
  id: string; title: string; charLimit: number; guide: string;
  draft: string; approved: boolean;
}
interface Props {
  presets: AIPreset[]; step?: TaskStep; task?: Task; company?: Company;
  initialPresetId?: string;
  onSave: (config: { presetId: string; sections: BPSection[]; prompt: string }) => void;
  onClose: () => void;
}

const DEF_SEC: Omit<BPSection, 'draft' | 'approved'>[] = [
  { id: 's1', title: '사업 개요', charLimit: 2000, guide: '기업 소개, 사업 배경, 핵심 아이디어를 서술' },
  { id: 's2', title: '기업 현황', charLimit: 1500, guide: '조직, 매출, 주요 실적, 보유 역량 정리' },
  { id: 's3', title: '사업 목표 및 추진전략', charLimit: 2500, guide: '정량 목표, 단계별 전략, 차별화 포인트' },
  { id: 's4', title: '시장 분석', charLimit: 2000, guide: '시장 규모, 경쟁 현황, 타겟 고객 분석' },
  { id: 's5', title: '추진 일정 및 소요예산', charLimit: 1500, guide: '월별 마일스톤, 예산 항목별 내역' },
  { id: 's6', title: '기대 효과', charLimit: 1000, guide: '매출·고용·기술 등 정량적 기대효과' },
];

const bb = 'border-box' as const;
const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 4 };
const fullBtn = (bg: string, dis?: boolean): React.CSSProperties => ({
  width: '100%', padding: 12, borderRadius: R.md, border: 'none',
  background: bg, color: '#fff', fontSize: T.md, fontWeight: 700,
  cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.5 : 1,
});

export default function AIBizplanModal({ presets, task, company, initialPresetId, onSave, onClose }: Props) {
  const [phase, setPhase] = useState<'settings' | 'editing'>('settings');
  const [presetId, setPresetId] = useState(initialPresetId || (presets[0]?.id ?? ''));
  const [extraPrompt, setExtraPrompt] = useState('');
  const [sections, setSections] = useState<BPSection[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [revNote, setRevNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);

  const preset = useMemo(() => presets.find(p => p.id === presetId), [presets, presetId]);
  const allApproved = sections.length > 0 && sections.every(s => s.approved);
  const sec = sections[activeIdx];

  function handleStart() {
    if (!presetId) return;
    setSections(DEF_SEC.map(s => ({ ...s, draft: '', approved: false })));
    setActiveIdx(0); setPhase('editing');
  }
  async function genSection(idx: number) {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    const s = sections[idx];
    const draft = `[AI 생성 결과] ${s.title}\n\n기업명: ${company?.name || '-'}\n업종: ${company?.biz_type || '-'}\n\n${s.guide}에 맞춰 작성된 초안 내용이 여기에 표시됩니다.`;
    setSections(prev => prev.map((x, i) => i === idx ? { ...x, draft } : x));
    setLoading(false);
  }
  async function revSection(idx: number) {
    if (!revNote.trim()) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setSections(prev => prev.map((x, i) => i === idx ? { ...x, draft: x.draft + `\n\n[수정 반영: ${revNote}]` } : x));
    setRevNote(''); setLoading(false);
  }
  function approveSection(idx: number) {
    setSections(prev => prev.map((x, i) => i === idx ? { ...x, approved: true } : x));
    if (idx < sections.length - 1) setActiveIdx(idx + 1);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{ position: 'relative', background: C.white, borderRadius: R.lg, width: '100%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', position: 'sticky', top: 0, background: C.white, zIndex: 1 }}>
          <span style={{ fontSize: T.lg, fontWeight: 800, flex: 1 }}>AI 사업계획서 (섹션별)</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: T.xl, cursor: 'pointer', color: C.muted }}>x</button>
        </div>
        <div style={{ padding: 20 }}>
          {phase === 'settings' ? renderSettings() : reviewSent ? renderReviewSent() : renderEditor()}
        </div>
      </div>
      {loading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.lg, fontWeight: 700, color: C.purple }}>
          AI 작성 중...
        </div>
      )}
    </div>
  );

  // ═══ Phase 1: Settings ═══
  function renderSettings() {
    return (
      <div>
        <label style={lbl}>프리셋 선택</label>
        <select value={presetId} onChange={e => setPresetId(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: bb, marginBottom: 16 }}>
          <option value="">선택</option>
          {presets.filter(p => p.type === 'ai_bizplan' && p.is_active).map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <label style={{ ...lbl, marginBottom: 6 }}>작성 섹션 ({DEF_SEC.length}개)</label>
        <div style={{ padding: 10, borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}`, marginBottom: 16 }}>
          {DEF_SEC.map((s, i) => (
            <div key={s.id} style={{ fontSize: T.xs, color: C.sub, padding: '3px 0', display: 'flex', gap: 6 }}>
              <span style={{ color: C.accent, fontWeight: 700 }}>{i + 1}.</span>
              <span>{s.title}</span>
              <span style={{ marginLeft: 'auto', color: C.purple, fontSize: T.xs }}>{s.charLimit}자</span>
            </div>
          ))}
        </div>
        <label style={lbl}>추가 지시사항</label>
        <textarea value={extraPrompt} onChange={e => setExtraPrompt(e.target.value)}
          placeholder="예: 기술 혁신 부분을 강조해주세요, 매출 목표 20억 반영" rows={3}
          style={{ width: '100%', padding: '9px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: bb, marginBottom: 20 }} />
        <button onClick={handleStart} disabled={!presetId}
          style={{ ...fullBtn(!presetId ? C.border : `linear-gradient(135deg,${C.accent},${C.primary})`, !presetId), padding: '12px 0', fontSize: T.md }}>
          시작
        </button>
      </div>
    );
  }

  // ═══ Phase 2: Section Editor ═══
  function renderEditor() {
    const badge = (bg: string, col: string, text: string): React.ReactNode => (
      <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: R.sm, background: bg, color: col, fontSize: T.xs, fontWeight: 600 }}>{text}</span>
    );
    return (
      <div>
        {/* Section tabs */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 14, overflowX: 'auto', paddingBottom: 4 }}>
          {sections.map((s, i) => (
            <div key={s.id} onClick={() => setActiveIdx(i)} style={{
              flexShrink: 0, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              border: `2px solid ${activeIdx === i ? C.purple : 'transparent'}`,
              background: s.approved ? C.greenLt : s.draft ? C.purpleLt : C.bgAlt,
              fontSize: T.xs, fontWeight: activeIdx === i ? 700 : 400,
              color: s.approved ? C.green : s.draft ? C.purple : C.muted,
            }}>
              {s.approved ? '\u2713 ' : s.draft ? '\u270F ' : ''}{s.title.slice(0, 7)}
            </div>
          ))}
        </div>

        {sec && (<div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: T.md + 1, fontWeight: 800 }}>{activeIdx + 1}. {sec.title}</span>
            {badge(C.purpleLt, C.purple, `${sec.charLimit}자 목표`)}
            {sec.approved && badge(C.greenLt, C.green, '확정')}
          </div>
          <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 8 }}>{sec.guide}</div>

          {!sec.draft ? (
            <div style={{ padding: 28, textAlign: 'center', borderRadius: 10, border: `2px dashed ${C.border}`, background: C.bgAlt }}>
              <div style={{ fontSize: T.sm, color: C.muted, marginBottom: 12 }}>이 섹션의 초안을 AI로 생성합니다</div>
              <button onClick={() => genSection(activeIdx)}
                style={{ padding: '10px 20px', borderRadius: R.md, border: 'none', background: C.accent, color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer' }}>
                초안 생성</button>
            </div>
          ) : (<div>
            <textarea value={sec.draft} onChange={e => setSections(prev => prev.map((x, i) => i === activeIdx ? { ...x, draft: e.target.value } : x))}
              rows={9} style={{ width: '100%', padding: 10, borderRadius: R.md, border: `1px solid ${sec.approved ? C.green : C.border}`, fontSize: T.sm, lineHeight: 1.8, outline: 'none', resize: 'vertical', boxSizing: bb, background: sec.approved ? '#f0fdf4' : '#fff' }} />
            <div style={{ fontSize: T.xs, textAlign: 'right', marginTop: 2, color: sec.draft.length > sec.charLimit * 1.2 ? C.red : sec.draft.length < sec.charLimit * 0.6 ? C.orange : C.green }}>
              {sec.draft.length}자 / 목표 {sec.charLimit}자
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input value={revNote} onChange={e => setRevNote(e.target.value)} placeholder="수정 지시 (예: 더 구체적인 수치 추가)"
                style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
              <button onClick={() => revSection(activeIdx)} disabled={!revNote.trim() || loading}
                style={{ padding: '7px 12px', borderRadius: 6, border: 'none', background: C.purple, color: '#fff', fontSize: T.xs, cursor: 'pointer', opacity: (!revNote.trim() || loading) ? 0.5 : 1 }}>수정</button>
              <button onClick={() => genSection(activeIdx)}
                style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer' }}>재생성</button>
            </div>
            {!sec.approved && (
              <button onClick={() => approveSection(activeIdx)} style={fullBtn(C.accent)}>
                이 섹션 확정{activeIdx < sections.length - 1 ? ' \u2192 다음 섹션' : ''}
              </button>
            )}
          </div>)}

          {/* Prev / Next */}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {[{ d: -1, label: '\u2190 이전', dis: activeIdx === 0 }, { d: 1, label: '다음 \u2192', dis: activeIdx === sections.length - 1 }].map(n => (
              <button key={n.label} onClick={() => setActiveIdx(Math.max(0, Math.min(sections.length - 1, activeIdx + n.d)))} disabled={n.dis}
                style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer', opacity: n.dis ? 0.4 : 1 }}>{n.label}</button>
            ))}
          </div>
        </div>)}

        {allApproved && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: C.greenLt, border: `1px solid ${C.green}44` }}>
            <div style={{ fontSize: T.md + 1, fontWeight: 800, color: C.green, marginBottom: 4 }}>전 섹션 완료!</div>
            <div style={{ fontSize: T.sm, color: C.sub, marginBottom: 10 }}>고객사에 검토를 요청하세요</div>
            <button onClick={() => setReviewSent(true)} style={fullBtn(C.accent)}>고객사 검토 요청 발송</button>
          </div>
        )}
      </div>
    );
  }

  // ═══ Review Sent ═══
  function renderReviewSent() {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>{'📬'}</div>
        <div style={{ fontSize: T.lg + 1, fontWeight: 800, marginBottom: 4 }}>검토 요청 발송됨</div>
        <div style={{ fontSize: T.sm, color: C.muted, marginBottom: 16 }}>{company?.name || '-'} 대표님께 사업계획서 초안 검토 요청</div>
        <div style={{ padding: 10, borderRadius: R.md, background: C.accentLt, fontFamily: 'monospace', fontSize: T.xs, color: C.accent, marginBottom: 16 }}>
          roum-partners.com/review/{task?.id || 'token'}
        </div>
        <button onClick={() => onSave({ presetId, sections, prompt: extraPrompt })} style={fullBtn(C.accent)}>검토 완료 확인</button>
      </div>
    );
  }
}
